/**
 * noteConfigStore (demo-only)
 * ──────────────────────────────────────────────────────────────────────
 * Unified store for Lumina note + visit-type configuration.
 *
 * Keys (all in localStorage):
 *   DEMO_NOTE_GLOBAL_v1   — { formatId, sections, copyForward, assessments }
 *   DEMO_NOTE_VISIT_v1    — { [visitTypeId]: { useGlobalDefault, formatId, sections, copyForward, assessments, linkedTemplateIds } }
 *   DEMO_NOTE_TEMPLATES_v1 — array of { id, label, body, sections, linkedVisitTypes }
 *
 * Real mode never touches these keys; consumers gate via useAppMode.
 */
import { NOTE_FORMATS, noteFormatById, COPY_FORWARD_RULES } from './noteFormatCatalog';

const KEYS = {
  global: 'DEMO_NOTE_GLOBAL_v1',
  visit: 'DEMO_NOTE_VISIT_v1',
  templates: 'DEMO_NOTE_TEMPLATES_v1',
  customTemplates: 'DEMO_NOTE_CUSTOM_TPL_v1',
  orgSharedTemplates: 'DEMO_NOTE_ORG_SHARED_TPL_v1',
};

const ls = () => { try { return window.localStorage; } catch { return null; } };
const readJSON = (k, def) => {
  const s = ls(); if (!s) return def;
  try { const v = s.getItem(k); return v ? JSON.parse(v) : def; } catch { return def; }
};
const writeJSON = (k, v) => { const s = ls(); if (!s) return; try { s.setItem(k, JSON.stringify(v)); } catch {} };

// ── Default builders ─────────────────────────────────────────────────
export const buildDefaultConfig = (formatId = 'soap') => {
  const fmt = noteFormatById(formatId);
  const sections = fmt.sections.map((s, i) => ({
    ...s,
    order: i,
    enabled: true,
    promptHint: s.promptHint || '',
    subheadings: (s.subheadings || []).map((sub) => ({ ...sub })),
  }));
  const sectionsEnabled = {};
  const rules = {};
  for (const s of sections) {
    sectionsEnabled[s.id] = true;
    rules[s.id] = (s.id === 'chiefComplaint' || s.id === 'narrative') ? 'never' : 'same-visit';
  }
  return {
    formatId,
    sections,
    copyForward: { sectionsEnabled, rules, conditions: {} },
    assessments: { seeds: [{ id: 'a-1', label: 'A/P — Primary' }] },
  };
};

// ── Global default ────────────────────────────────────────────────────
export const getGlobalConfig = () => readJSON(KEYS.global, null) || buildDefaultConfig('soap');
export const saveGlobalConfig = (cfg) => writeJSON(KEYS.global, cfg);

// ── Per-visit-type ────────────────────────────────────────────────────
export const getVisitConfig = (visitTypeId) => {
  const all = readJSON(KEYS.visit, {});
  if (all[visitTypeId]) return all[visitTypeId];
  return { useGlobalDefault: true, ...buildDefaultConfig(getGlobalConfig().formatId), linkedTemplateIds: [] };
};
export const saveVisitConfig = (visitTypeId, cfg) => {
  const all = readJSON(KEYS.visit, {});
  all[visitTypeId] = cfg;
  writeJSON(KEYS.visit, all);
};
export const resetVisitConfig = (visitTypeId) => {
  const all = readJSON(KEYS.visit, {});
  delete all[visitTypeId];
  writeJSON(KEYS.visit, all);
};

// ── Templates ─────────────────────────────────────────────────────────
const DEFAULT_TEMPLATES = [
  { id: 'fu_generic',    label: 'Generic follow-up',  body: 'Patient returns for follow-up of {conditions}. No new concerns…',           linkedVisitTypes: ['follow-up'] },
  { id: 'annual_adult',  label: 'Adult annual visit', body: 'Annual wellness visit. Patient feels well. Screenings reviewed…',           linkedVisitTypes: ['wellness'] },
  { id: 'telehealth',    label: 'Telehealth visit',   body: 'Encounter conducted via HIPAA-compliant video. Patient verified location.', linkedVisitTypes: ['telehealth'] },
  { id: 'pre_op',        label: 'Pre-op evaluation',  body: 'Pre-op H&P for scheduled {procedure}. ASA class {asa}, METs {mets}…',       linkedVisitTypes: ['pre-op'] },
];
export const getTemplates = () => readJSON(KEYS.templates, null) || DEFAULT_TEMPLATES;
export const saveTemplates = (t) => writeJSON(KEYS.templates, t);

// ── Custom templates (named snapshots of a Custom note format) ────────
// Shape: { id, label, baseFormatId, sections, copyForward, assessments,
//          createdAt, ownerId, ownerName, sharedWithOrg }
export const getCustomTemplates = () => readJSON(KEYS.customTemplates, []);
export const saveCustomTemplates = (t) => writeJSON(KEYS.customTemplates, t);
export const upsertCustomTemplate = (tpl) => {
  const all = getCustomTemplates();
  const idx = all.findIndex((t) => t.id === tpl.id);
  if (idx >= 0) all[idx] = tpl; else all.push(tpl);
  saveCustomTemplates(all);
  return all;
};
export const deleteCustomTemplate = (id) => {
  const next = getCustomTemplates().filter((t) => t.id !== id);
  saveCustomTemplates(next);
  // Cascade: also remove from org library if it was shared.
  unshareTemplateFromOrg(id);
  return next;
};

// ── Org-shared templates library ──────────────────────────────────────
// Same shape as custom templates but `sharedWithOrg: true` always; the
// library aggregates entries from every provider in the org. In demo mode
// it is seeded with a couple of teammate-owned entries so a single user
// can experience the share/clone flow without multi-account setup.
const DEFAULT_ORG_SHARED = [
  {
    id: 'org-tpl-cardio-consult',
    label: 'Cardiology Consult — Dr. Patel',
    description: 'Use for new cardiology referrals — emphasizes auscultation findings and JVP.',
    baseFormatId: 'consult',
    ownerId: 'demo-teammate-1',
    ownerName: 'Dr. Anika Patel',
    sharedWithOrg: true,
    createdAt: '2026-01-12T14:20:00.000Z',
    sections: [
      { id: 'cc',     label: 'Reason for Consultation', enabled: true, order: 0, subheadings: [] },
      { id: 'hpi',    label: 'HPI',                     enabled: true, order: 1, subheadings: [] },
      { id: 'cv',     label: 'Cardiac Exam',            enabled: true, order: 2,
        subheadings: [
          { id: 'cv-aus',  label: 'Auscultation' },
          { id: 'cv-pmi',  label: 'PMI / Apex' },
          { id: 'cv-jvp',  label: 'JVP' },
        ] },
      { id: 'imp',    label: 'Impression',              enabled: true, order: 3, subheadings: [] },
      { id: 'rec',    label: 'Recommendations',         enabled: true, order: 4, subheadings: [] },
    ],
    copyForward: { sectionsEnabled: { cc: false, hpi: true, cv: true, imp: true, rec: true }, rules: { cc: 'never', hpi: 'same-visit', cv: 'same-visit', imp: 'same-visit', rec: 'same-visit' } },
    assessments: { seeds: [{ id: 'a-1', label: 'A/P — Cardiology' }] },
  },
  {
    id: 'org-tpl-peds-wellchild',
    label: 'Well-Child 0–24mo — Dr. Lee',
    description: 'Pediatric well-child checks under 2 years — feeding, sleep, milestones + growth curve prompts.',
    baseFormatId: 'soap',
    ownerId: 'demo-teammate-2',
    ownerName: 'Dr. Marcus Lee',
    sharedWithOrg: true,
    createdAt: '2026-02-04T09:05:00.000Z',
    sections: [
      { id: 'subjective', label: 'Subjective', enabled: true, order: 0,
        subheadings: [
          { id: 's-feed',  label: 'Feeding' },
          { id: 's-sleep', label: 'Sleep' },
          { id: 's-dev',   label: 'Development' },
        ] },
      { id: 'objective',  label: 'Objective',  enabled: true, order: 1,
        subheadings: [
          { id: 'o-vit',  label: 'Vitals + Growth Curves' },
          { id: 'o-pe',   label: 'Physical Exam' },
        ] },
      { id: 'assessment', label: 'Assessment',         enabled: true, order: 2, subheadings: [] },
      { id: 'plan',       label: 'Plan / Anticipatory Guidance', enabled: true, order: 3, subheadings: [] },
    ],
    copyForward: { sectionsEnabled: { subjective: true, objective: false, assessment: true, plan: true }, rules: { subjective: 'same-visit', objective: 'never', assessment: 'same-visit', plan: 'same-visit' } },
    assessments: { seeds: [{ id: 'a-1', label: 'A/P — Well child' }] },
  },
];
export const getOrgSharedTemplates = () => readJSON(KEYS.orgSharedTemplates, null) || DEFAULT_ORG_SHARED;
export const saveOrgSharedTemplates = (t) => writeJSON(KEYS.orgSharedTemplates, t);
export const shareTemplateWithOrg = (tpl) => {
  const all = getOrgSharedTemplates();
  const idx = all.findIndex((t) => t.id === tpl.id);
  const stamped = { ...tpl, sharedWithOrg: true };
  if (idx >= 0) all[idx] = stamped; else all.push(stamped);
  saveOrgSharedTemplates(all);
  return all;
};
export const unshareTemplateFromOrg = (id) => {
  const next = getOrgSharedTemplates().filter((t) => t.id !== id);
  saveOrgSharedTemplates(next);
  return next;
};

// ── Section / subheading mutations (pure functions) ───────────────────
export const reorderSections = (sections, fromIdx, toIdx) => {
  const next = [...sections];
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved);
  return next.map((s, i) => ({ ...s, order: i }));
};
export const renameSection = (sections, id, label) => sections.map((s) => (s.id === id ? { ...s, label } : s));
export const toggleSection = (sections, id) => sections.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s));
export const removeSection = (sections, id) => sections.filter((s) => s.id !== id).map((s, i) => ({ ...s, order: i }));
export const addSection = (sections, label = 'New Section') => [
  ...sections,
  { id: `section-${Date.now()}`, label, enabled: true, order: sections.length, promptHint: '', subheadings: [] },
];
export const setPromptHint = (sections, id, promptHint) =>
  sections.map((s) => (s.id === id ? { ...s, promptHint } : s));
export const addSubheading = (sections, sectionId, label = 'New subheading') =>
  sections.map((s) => (s.id === sectionId
    ? { ...s, subheadings: [...(s.subheadings || []), { id: `${sectionId}-sub-${Date.now()}`, label }] }
    : s));
export const renameSubheading = (sections, sectionId, subId, label) =>
  sections.map((s) => (s.id === sectionId
    ? { ...s, subheadings: (s.subheadings || []).map((x) => (x.id === subId ? { ...x, label } : x)) }
    : s));
export const removeSubheading = (sections, sectionId, subId) =>
  sections.map((s) => (s.id === sectionId
    ? { ...s, subheadings: (s.subheadings || []).filter((x) => x.id !== subId) }
    : s));
export const reorderSubheadings = (sections, sectionId, fromIdx, toIdx) =>
  sections.map((s) => {
    if (s.id !== sectionId) return s;
    const next = [...(s.subheadings || [])];
    const [m] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, m);
    return { ...s, subheadings: next };
  });

// ── Assessment seed helpers (cardiology-style) ────────────────────────
export const addAssessment = (seeds, label = 'A/P — New block') => [...seeds, { id: `a-${Date.now()}`, label }];
export const removeAssessment = (seeds, id) => seeds.filter((s) => s.id !== id);
export const renameAssessment = (seeds, id, label) => seeds.map((s) => (s.id === id ? { ...s, label } : s));

export { NOTE_FORMATS, noteFormatById, COPY_FORWARD_RULES };
